import { ActivityDto } from 'src/app/types/dto/activity.dto'
import { ChartJobFieldDto } from 'src/app/types/dto/chart-job-field.dto'
import { DashboardJobLookupDto } from 'src/app/types/dto/dashboard-job-lookup.dto'
import { ForecastJobData } from 'src/app/types/dto/forecast-job-data.dto'
import { ForecastTaskData } from 'src/app/types/dto/forecast-task-data.dto'
import { Holiday } from 'src/app/types/dto/holiday.dto'
import { IncomeInvoiceStatus, IncomeInvoiceType } from 'src/app/types/dto/income-invoice.dto'
import { OrderLineExtraDto } from 'src/app/types/dto/order-line-extra.dto'
import { TaskMaster, TaskStatus } from 'src/app/types/dto/tasks.dto'
import { UserDto, UserTypeEnum } from 'src/app/types/dto/user.dto'
import { environment } from 'src/environments/environment'
import {
  activityNames,
  divisions,
  extraCategories,
  extraScenarios,
  houseTypes,
  locations,
  managers,
  salesReps,
  scenarios,
  taskDefinitions,
  trackingFields,
  trackingValues,
  variationScenarios,
} from './demo-api.fixtures.data'
import type { DemoCashflow, DemoClaim, DemoJob, DemoTask } from './demo-api.fixtures.types'

const money = (amount: number): number => Math.round(amount * 100) / 100
const roleName = (job: DemoJob, role: string): string =>
  job.jobRoles.find((assignment) => assignment.companyRoleDescription === role)?.name ?? ''

export function createDemoFixtures(asOf = new Date()) {
  const today = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate(), 12)
  const date = (days: number): Date => new Date(today.getFullYear(), today.getMonth(), today.getDate() + days, 12)
  const iso = (days: number): string => date(days).toISOString()
  // Fixtures are unreachable when demoCompanyId is null; zero keeps their DTO fields numeric in non-demo builds.
  const companyId = environment.demoCompanyId ?? 0

  const jobs: DemoJob[] = scenarios.map((scenario, index) => {
    const location = locations[scenario.location]
    const clientLocation = locations[scenario.clientLocation]
    const houseType = houseTypes[scenario.houseType]
    const signed = scenario.state !== 'sales'
    const cancelled = scenario.state === 'cancelled'
    const delay = scenario.state === 'hold' ? 60 : scenario.state === 'problem' ? 30 : 0
    const completed = !cancelled && scenario.startDay + 187 + delay < 0
    const contractTotalExcGST = money(scenario.price / 1.1)
    const budgetAmount = money(contractTotalExcGST * (1 - scenario.margin / 100))
    const jobMargin = money(contractTotalExcGST - budgetAmount)
    const contractDay = Math.min(-7 - index * 3, scenario.startDay - 90)
    const started = signed && !cancelled && scenario.startDay <= 0
    const titleCompleteDay = scenario.startDay - 45
    const maintenanceCompleteDay = scenario.startDay + 277
    const wipWriteOutDay = scenario.startDay + 217
    const currentActivityStartDay = cancelled
      ? -35
      : completed
        ? scenario.startDay + 180
        : !signed
          ? -5
          : scenario.startDay > 0
            ? contractDay
            : scenario.startDay > -24
              ? scenario.startDay
              : scenario.startDay + 24
    const currentActivityDueDay = cancelled
      ? null
      : completed
        ? scenario.startDay + 194
        : !signed
          ? 7
          : scenario.startDay > 0
            ? scenario.startDay - 1
            : scenario.startDay > -24
              ? scenario.startDay + 24
              : scenario.startDay + 180

    return {
      id: 7301 + index,
      jobNumber: `DEMO-${String(index + 1).padStart(3, '0')}`,
      contractName: `${scenario.name} Residence`,
      jobAddressString: `${10 + index} Example Street, ${location.suburb} WA ${location.postcode}`,
      contractAddressString: `${30 + index} Sample Road, ${clientLocation.suburb} WA`,
      council: location.council,
      estate: scenario.division === 'customHomes' ? '' : location.estate,
      division: divisions[scenario.division],
      houseType: houseType.description,
      houseMasterID: 7401 + index,
      landZoning: 'landZoning' in scenario ? scenario.landZoning : 'Residential',
      landTitleType: houseType.landTitleType,
      landType: scenario.division === 'customHomes' ? 'Private' : 'Developer',
      jobSuburbTown: location.suburb,
      stage: cancelled
        ? 'Cancelled'
        : !signed
          ? 'Pending Sale'
          : !started
            ? 'Pre-Construction'
            : !completed
              ? 'Construction'
              : maintenanceCompleteDay <= 0
                ? 'Warranty'
                : 'Maintenance',
      clientSuburbTown: clientLocation.suburb,
      currentActivity: activityNames[cancelled ? 5 : completed ? 4 : !signed ? 0 : scenario.startDay > 0 ? 1 : scenario.startDay > -24 ? 2 : 3],
      tradeRegion: location.region,
      houseArea: `${houseType.baseArea + index * 2} m2`,
      blockFrontage: `${houseType.frontage} m`,
      isActive: !completed && !cancelled,
      isLocked: completed || cancelled,
      isOnHold: scenario.state === 'hold',
      isGSTFree: false,
      lockedDate: completed ? date(scenario.startDay + 201) : cancelled ? date(-35) : null,
      salesDate: date(signed ? contractDay - 20 : -3),
      contractSignedDate: signed ? date(contractDay) : null,
      salesQuoteSignedDate: signed ? date(contractDay - 15) : null,
      quoteDate: date(signed ? contractDay - 30 : -5),
      contractQuoteDate: signed ? date(contractDay - 10) : null,
      titleDueDate: date(scenario.startDay - 50),
      titleCompleteDate: signed && titleCompleteDay <= 0 ? date(titleCompleteDay) : null,
      depositBalanceDue: signed ? date(contractDay + 7) : null,
      estimatingCostingDate: signed ? date(contractDay - 5) : null,
      completionDate: completed ? date(scenario.startDay + 187) : null,
      cancellationDate: cancelled ? date(-35) : null,
      handoverDate: completed ? date(scenario.startDay + 194) : null,
      targetStartDate: cancelled ? null : date(scenario.startDay),
      targetCompletionDate: cancelled ? null : date(scenario.startDay + 187),
      forecastCompletionDate: cancelled ? null : date(scenario.startDay + 187 + delay),
      siteStartDate: !cancelled && scenario.startDay <= 0 ? date(scenario.startDay) : null,
      forecastSiteStartDate: cancelled ? null : date(scenario.startDay),
      ordersLockedDate: signed ? date(scenario.startDay - 21) : null,
      currentActivityStartDate: date(currentActivityStartDay),
      currentActivityDueDate: currentActivityDueDay === null ? null : date(currentActivityDueDay),
      salesQuoteDueDate: signed ? date(contractDay - 15) : date(7),
      contractQuoteDueDate: signed ? date(contractDay - 5) : null,
      holdDate: scenario.state === 'hold' ? date(-45) : null,
      maintenanceCompleteDate: maintenanceCompleteDay <= 0 ? date(maintenanceCompleteDay) : null,
      lastCustomerNotificationDateTime: signed && !cancelled ? date(-7 - index) : null,
      preconstructionCompleteDate: started ? date(scenario.startDay - 1) : null,
      siteStartInDate: started ? date(scenario.startDay) : null,
      siteStartOutDate: started ? date(scenario.startDay + 3) : null,
      wipWriteOutDate: completed && wipWriteOutDay <= 0 ? date(wipWriteOutDay) : null,
      contractPrice: signed ? scenario.price : 0,
      depositAmount: signed ? money(scenario.price * 0.05) : 0,
      basePrice: money(scenario.price * 0.9),
      budgetAmount: signed ? budgetAmount : 0,
      jobMargin: signed ? jobMargin : 0,
      variationMargin: 0,
      gstRate: 10,
      depositPaid: signed && scenario.state !== 'waiting' ? money(scenario.price * 0.05) : 0,
      salesQuotePrice: scenario.price,
      estimatingTotalCost: signed ? budgetAmount : 0,
      salesJobCostExGst: signed ? budgetAmount : 0,
      contractJobCostExGst: signed ? budgetAmount : 0,
      postContractVariations: 0,
      contractTotalExcGST: signed ? contractTotalExcGST : 0,
      jobCostExcGST: completed ? budgetAmount : signed && scenario.startDay < 0 ? money(budgetAmount * Math.min(0.95, -scenario.startDay / 187)) : 0,
      orderExtraTotal: 0,
      extraIncomeIncGST: 0,
      markUpPercent: signed ? money(jobMargin / budgetAmount * 100) : 0,
      marginPercent: signed ? scenario.margin : 0,
      jobRoles: [
        { companyRoleDescription: 'Site Manager', name: scenario.manager ? managers[scenario.manager] : '' },
        { companyRoleDescription: 'Sales Consultant', name: salesReps[scenario.salesRep] },
      ],
    }
  })

  const activities: ActivityDto[] = activityNames.map((description, index) => ({
    id: 7310 + index, companyId, activityCode: String((index + 1) * 10),
    description, isActive: true, isDefaultForVariations: false, isDefaultForCreditVariations: false,
  }))
  const taskMasters: TaskMaster[] = taskDefinitions.map((task) => ({
    id: task.id, isActive: true, taskTitle: task.title, taskTypeDescription: task.type, taskCode: task.code,
  }))
  const tasks: DemoTask[] = []
  const forecastTasks: ForecastTaskData[] = []
  const cashflow: DemoCashflow[] = []
  const claims: DemoClaim[] = []

  jobs.forEach((job, jobIndex) => {
    const scenario = scenarios[jobIndex]
    if (scenario.state === 'sales') return
    const cancelled = scenario.state === 'cancelled'
    const siteManager = roleName(job, 'Site Manager')
    const contractDay = Math.min(-7 - jobIndex * 3, scenario.startDay - 90)
    const addClaim = (type: IncomeInvoiceType, amount: number, invoiceDay: number, status: IncomeInvoiceStatus) => {
      const totalIncGST = money(amount)
      const totalExGST = money(totalIncGST / 1.1)
      const createDate = date(Math.min(0, invoiceDay + 2))
      claims.push({
        id: 75000 + claims.length, jobId: job.id, jobNumber: job.jobNumber, incomeInvoiceTypeId: type,
        invoiceDate: date(invoiceDay), dueDate: date(invoiceDay + 14), createDate,
        postedDate: status === IncomeInvoiceStatus.Posted ? createDate : null,
        totalExGST, totalIncGST, totalGST: money(totalIncGST - totalExGST),
        incomeInvoiceStatusId: status, division: job.division, stage: job.stage, siteManager,
      })
    }
    addClaim(IncomeInvoiceType.Deposit, job.depositAmount ?? 0, contractDay + 1,
      scenario.state === 'waiting' ? IncomeInvoiceStatus.Sent : IncomeInvoiceStatus.Posted)

    taskDefinitions.forEach((definition, taskIndex) => {
      if (definition.id === 7302 && scenario.houseType === 'townhouse') return
      if (definition.id === 7309 && scenario.houseType !== 'townhouse') return
      const notApplicable = definition.code === 'LANDSCAPE' && scenario.houseType === 'townhouse'
      const plannedStart = scenario.startDay + definition.offset
      const paused = scenario.state === 'hold' && plannedStart >= -30
      const problem = scenario.state === 'problem' && definition.code === 'LOCKUP'
      const delay = paused ? 60 : scenario.state === 'problem' && definition.offset >= 100 ? 30 : 0
      const start = plannedStart + (problem ? 0 : delay)
      const end = plannedStart + definition.duration + delay
      const status = cancelled && definition.code !== 'APPROVAL' ? TaskStatus.Cancelled
        : notApplicable ? TaskStatus['Not Applicable']
        : paused ? TaskStatus.Hold
        : problem ? TaskStatus.Problem
        : scenario.state === 'waiting' && definition.code === 'APPROVAL' ? TaskStatus.Waiting
        : end <= 0 ? TaskStatus.Completed
        : start <= 0 ? TaskStatus['In Progress'] : TaskStatus['Not Started']
      const taskId = 74000 + jobIndex * 10 + taskIndex
      tasks.push({
        id: taskId, jobId: job.id, jobNumber: job.jobNumber, taskMasterId: definition.id,
        taskMasterTitle: definition.title, startDate: iso(start),
        endDate: status === TaskStatus.Completed ? iso(end) : '',
        sentDate: start <= 7 && !notApplicable ? iso(Math.min(0, start - 7)) : '',
        statusId: status, allocatedTo: definition.code === 'APPROVAL' ? roleName(job, 'Sales Consultant') : siteManager,
        division: job.division, stage: job.stage, vendor: definition.vendor,
        salesRep: roleName(job, 'Sales Consultant'), siteManager,
      })
      if (cancelled || notApplicable) return
      forecastTasks.push({
        id: taskId, jobId: job.id, taskMasterId: definition.id,
        forecastStartDate: iso(start), forecastCompletionDate: iso(end),
        targetStartDate: iso(plannedStart), targetCompletionDate: iso(plannedStart + definition.duration),
        taskTitle: definition.title,
      })
      if (definition.share > 0 && end <= 0) {
        addClaim(IncomeInvoiceType.Claim, (job.contractPrice ?? 0) * definition.share, end,
          end < -14 ? IncomeInvoiceStatus.Posted : end < -3 ? IncomeInvoiceStatus.Sent : IncomeInvoiceStatus.Created)
      }
      if (job.isOnHold || definition.type === 'Pre-Construction' || end + 14 < 0) return
      const incomeReceivable = money((job.contractPrice ?? 0) * definition.share)
      const costSubcontractor = -money((job.budgetAmount ?? 0) * (definition.share || 0.025) * 0.55)
      const costSupplier = -money((job.budgetAmount ?? 0) * definition.share * 0.45)
      cashflow.push({
        id: 76000 + cashflow.length, jobCashFlowId: 7700 + jobIndex, jobNumber: job.jobNumber,
        jobDescription: job.contractName, division: job.division, stage: job.stage, taskId: definition.id + 10000, taskMasterId: definition.id,
        taskDescription: definition.title, isPracticalCompletionTask: definition.code === 'PC',
        forecastStartDate: iso(start), forecastCompletionDate: iso(end), forecastPaymentDate: iso(end + 14),
        invoiceDate: incomeReceivable ? iso(end) : null,
        isForecastStart: definition.code === 'START', costSubcontractor, costSupplier, incomeReceivable,
        incomeReceivableExGst: money(incomeReceivable / 1.1),
        totalFlow: money(incomeReceivable + (costSubcontractor + costSupplier) * 1.1),
        totalFlowExGst: money(incomeReceivable / 1.1 + costSubcontractor + costSupplier),
        activityToSetEndDateId: null,
      })
    })
    const variation = variationScenarios.find((item) => item.job === scenario.key)
    if (variation) {
      addClaim(IncomeInvoiceType.Variation, variation.amount, variation.days,
        variation.status === 'created' ? IncomeInvoiceStatus.Created : IncomeInvoiceStatus.Posted)
    }
    if (cancelled) addClaim(IncomeInvoiceType.Deposit, -(job.depositAmount ?? 0), -35, IncomeInvoiceStatus.Posted)
  })

  const forecastJobs: ForecastJobData[] = jobs.filter((job) => forecastTasks.some((task) => task.jobId === job.id)).map((job) => ({
    jobId: job.id, jobNumber: job.jobNumber, siteManager: roleName(job, 'Site Manager'),
    division: job.division, stage: job.stage, houseType: job.houseType, contractSignedDate: job.contractSignedDate!,
  }))

  const jobsByKey = new Map(scenarios.map((scenario, index) => [scenario.key, jobs[index]]))
  const extras: OrderLineExtraDto[] = extraScenarios.map((extra, index) => {
    const job = jobsByKey.get(extra.job)!
    const category = extraCategories[extra.category]
    const invoiced = extra.days < -20
    return {
      id: 79000 + index, jobId: job.id, jobNumber: job.jobNumber,
      costCentreDescription: category.costCentreDescription, costCentreId: category.costCentreId,
      createDate: iso(extra.days), createUserId: 8201,
      description: extra.amount === 0 ? 'Like-for-like fitting substitution' : extra.amount < 0 ? 'Remove feature lighting' : category.positiveDescription,
      division: job.division, stage: job.stage, invoiceId: invoiced ? 83000 + index : null,
      invoiceVendorCode: invoiced ? category.vendorCode : null,
      invoiceVendorName: invoiced ? category.vendorName : null,
      modifiedDate: iso(Math.min(0, extra.days + 2)), modifiedUserId: 8201,
      orderLineId: 80000 + index, originalOrderLineId: null,
      phaseId: category.phaseId, poNumber: `PO-DEMO-${index + 1}`, purchaseOrderVendorId: category.purchaseOrderVendorId,
      siteManager: roleName(job, 'Site Manager'),
      varianceCodeDescription: extra.amount === 0 ? '' : extra.reason === 'Client selection' || extra.reason === 'Scope reduction' ? 'Client Variation' : 'Cost Overrun',
      varianceCodeId: extra.amount === 0 ? 3 : extra.reason === 'Client selection' || extra.reason === 'Scope reduction' ? 1 : 2,
      varianceReasonDescription: extra.reason, varianceReasonId: index + 1,
      varianceTotal: extra.amount, vendorCode: category.vendorCode, vendorName: category.vendorName,
    }
  })
  jobs.forEach((job) => {
    job.orderExtraTotal = money(extras.filter((extra) => extra.jobId === job.id).reduce((total, extra) => total + (extra.varianceTotal ?? 0), 0))
    job.extraIncomeIncGST = money(claims.filter((claim) => claim.jobId === job.id && claim.incomeInvoiceTypeId === IncomeInvoiceType.Variation)
      .reduce((total, claim) => total + claim.totalIncGST, 0))
    job.postContractVariations = job.extraIncomeIncGST
    job.variationMargin = money((job.extraIncomeIncGST ?? 0) / 1.1 - (job.orderExtraTotal ?? 0))
    job.contractTotalExcGST = money(((job.contractPrice ?? 0) + (job.postContractVariations ?? 0)) / 1.1)
    if (job.cancellationDate) job.depositPaid = 0
  })

  const jobFields: ChartJobFieldDto[] = trackingFields.flatMap((field) => jobs.map((job, jobIndex) => ({
    dataField: field.dataField, jobNumber: job.jobNumber, textValue: trackingValues[scenarios[jobIndex].key][field.dataField],
  })))

  // role ids/keys mirror the API's built-in roles; descriptions are the demo company's names
  const demoRoles = [
    { roleId: 1, key: 'siteManager', description: 'Site Manager' },
    { roleId: 4, key: 'salesRep', description: 'Sales Consultant' },
  ]
  const jobLookup: DashboardJobLookupDto = {
    roles: demoRoles.map((role) => ({
      ...role, isActive: true, isAssigned: jobs.some((job) => !!roleName(job, role.description)),
    })),
    jobs: jobs.map((job) => ({
      id: job.id, jobNumber: job.jobNumber, division: job.division, stage: job.stage,
      houseType: job.houseType, council: job.council, tradeRegion: job.tradeRegion,
      currentActivity: job.currentActivity, isOnHold: job.isOnHold, isActive: job.isActive,
      roles: demoRoles
        .map((role) => ({ roleId: role.roleId, name: roleName(job, role.description) }))
        .filter((assignment) => assignment.name),
    })),
  }

  const managerNames = Object.values(managers)
  const users: UserDto[] = [...managerNames, ...Object.values(salesReps)].map((fullName, index) => ({
    id: 8201 + index, email: `demo${index + 1}@example.invalid`, mobileNumber: '', title: '', fullName,
    firstName: fullName.split(' ')[0], middleName: '', lastName: fullName.split(' ')[1], suffix: '',
    positionDescription: index < managerNames.length ? 'Site Manager' : 'Sales Consultant',
    isActive: fullName !== managers.alexMorgan, isSuperUser: false, isServiceAccount: false,
    userTypeId: UserTypeEnum.Office, userType: 'Office',
  }))

  const holidays: Holiday[] = [-1, 0, 1].flatMap((yearOffset) => {
    const year = today.getFullYear() + yearOffset
    const christmas = new Date(year, 11, 25, 12)
    const boxingDay = new Date(year, 11, 26, 12)
    const dates = [
      christmas, boxingDay,
      ...(christmas.getDay() === 0 || christmas.getDay() === 6 ? [new Date(year, 11, 27, 12)] : []),
      ...(boxingDay.getDay() === 0 || boxingDay.getDay() === 6 ? [new Date(year, 11, 28, 12)] : []),
    ]
    return dates.map((holidayDate, index) => ({
      id: year * 100 + index, companyId, date: holidayDate.toISOString(),
      description: index === 0 ? 'Christmas Day' : index === 1 ? 'Boxing Day' : 'Additional public holiday',
      isUsedInCashflow: true, createDate: iso(-730), createUserId: 8201, modifiedDate: iso(-730), modifiedUserId: 8201,
    }))
  })

  return { jobs, jobLookup, activities, taskMasters, tasks, claims, cashflow, forecastTasks, forecastJobs, extras, trackingFields, jobFields, users, holidays }
}

export const {
  jobs: DEMO_JOBS, jobLookup: DEMO_JOB_LOOKUP, activities: DEMO_ACTIVITIES, taskMasters: DEMO_TASK_MASTERS,
  tasks: DEMO_TASKS, claims: DEMO_CLAIMS, cashflow: DEMO_CASHFLOW,
  forecastTasks: DEMO_FORECAST_TASKS, forecastJobs: DEMO_FORECAST_JOBS,
  extras: DEMO_EXTRAS, trackingFields: DEMO_TRACKING_FIELDS, jobFields: DEMO_JOB_FIELDS,
  users: DEMO_USERS, holidays: DEMO_HOLIDAYS,
} = createDemoFixtures()